import React from "react";
import {
  Collapse,
  Card,
  List,
  Typography,
  Tag,
  Divider,
  Space,
  Empty,
} from "antd";
import { useVisibilityTracking } from "../../../hooks/useVisibilityTracking";

const { Title, Text, Paragraph } = Typography;

const formatDate = (dateString) => {
  if (!dateString || dateString === "—" || dateString === "null") return "—";
  const str = String(dateString).trim();

  const fmt = new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" });

  const iso = /^(\d{4})-(\d{2})-(\d{2})$/;
  let m = str.match(iso);
  if (m) {
    const [, y, mo, d] = m;
    const date = new Date(Number(y), Number(mo) - 1, Number(d)); // local calendar date
    return fmt.format(date);
  }

  const mdy = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/;
  m = str.match(mdy);
  if (m) {
    let [, mm, dd, yy] = m;
    let year = Number(yy);
    if (yy.length === 2) year = year >= 70 ? 1900 + year : 2000 + year; // common pivot
    const date = new Date(year, Number(mm) - 1, Number(dd));
    return fmt.format(date);
  }

  const parsed = new Date(str);
  if (!isNaN(parsed)) return fmt.format(parsed);

  return str;
};

const KeyValueList = ({ items = [], size = "small", labelWidth = 220 }) => {
  const data = items.map((it) => ({
    label: it?.label,
    value:
      it?.value === 0 || it?.value === false
        ? String(it.value)
        : it?.value || "—",
  }));

  return (
    <List
      size={size}
      dataSource={data}
      renderItem={(row, idx) => (
        <List.Item key={idx}>
          <Space align="start" style={{ width: "100%", justifyContent: "space-between", gap: "1rem" }}>
            <Text type="secondary" style={{ width: '170px', whiteSpace: "nowrap", display: "block" }}>
              {row.label}
            </Text>
            <div style={{ flex: 1, wordBreak: "break-word", textAlign: "end" }}>{row.value}</div>
          </Space>
        </List.Item>
      )}
    />
  );
};

const KeyFindings = ({ findings }) => {
  if (!findings || typeof findings !== "object")
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  const {
    mental_status,
    visit_summary,
    diagnosis,
    treatment_plans,
    medications_mentioned,
    goals_progress,
  } = findings;

  return (
    <KeyValueList
      size="small"
      items={[
        { label: "Mental Status", value: mental_status },
        { label: "Visit Summary", value: visit_summary },
        { label: "Diagnosis", value: diagnosis },
        { label: "Treatment Plans", value: treatment_plans },
        { label: "Medications Mentioned", value: medications_mentioned },
        { label: "Goals Progress", value: goals_progress },
      ]}
    />
  );
};

const ClinicalTimelineSection = ({ data }) => {
  if (!Array.isArray(data) || data.length === 0) {
    return <Empty description="No clinical timeline entries" />;
  }

  return (
    <List
      itemLayout="vertical"
      dataSource={data}
      renderItem={(item, idx) => {
        const {
          date,
          visit_type,
          provider,
          chief_complaints,
          key_findings,
          significant_changes,
        } = item || {};

        return (
          <List.Item key={idx}>
            <Card
              title={
                <Space split={<Divider type="vertical" />}>
                  <Text strong>{formatDate(date)}</Text>
                  {visit_type ? <Tag>{visit_type}</Tag> : null}
                  {provider ? <Text type="secondary">{provider}</Text> : null}
                </Space>
              }
            >
              <KeyValueList
                size="middle"
                items={[{ label: "Chief Complaints", value: chief_complaints }]}
              />

              <Title level={5} style={{ marginTop: 0 }}>
                Key Findings
              </Title>
              <KeyFindings findings={key_findings} />

              <Divider style={{ margin: "16px 0" }} />

              <KeyValueList
                size="small"
                items={[{ label: "Significant Changes", value: significant_changes }]}
              />
            </Card>
          </List.Item>
        );
      }}
    />
  );
};

const CurrentStatusSection = ({ data }) => {
  if (!data || typeof data !== "object") {
    return <Empty description="No current status info" />;
  }

  const {
    active_medications,
    current_diagnoses,
    treatment_plan,
    next_appointment,
  } = data;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="Active Medications">
        {Array.isArray(active_medications) && active_medications.length > 0 ? (
          <List
            dataSource={active_medications}
            renderItem={(m, i) => (
              <List.Item key={i}>
                <Space direction="vertical" size={2} style={{ width: "100%" }}>
                  <Space wrap>
                    <Tag>{m?.name || "—"}</Tag>
                    {m?.dosage ? <Tag>{m.dosage}</Tag> : null}
                  </Space>
                  <Text type="secondary">
                    {m?.indication ? m.indication : "—"}
                  </Text>
                </Space>
              </List.Item>
            )}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No active medications"
          />
        )}
      </Card>

      <Card title="Current Diagnoses">
        {Array.isArray(current_diagnoses) && current_diagnoses.length > 0 ? (
          <List
            dataSource={current_diagnoses}
            renderItem={(d, i) => (
              <List.Item key={i}>
                <Space>
                  <Tag color="blue">{d?.diagnosis || "—"}</Tag>
                  {d?.code ? <Tag>{d.code}</Tag> : null}
                </Space>
              </List.Item>
            )}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No current diagnoses"
          />
        )}
      </Card>

      <Card title="Treatment Plan">
        <Paragraph style={{ marginBottom: 0 }}>
          {treatment_plan && String(treatment_plan).trim().length > 0
            ? treatment_plan
            : "—"}
        </Paragraph>
      </Card>

      <Card title="Next Appointment">
        <KeyValueList
          size="small"
          items={[
            {
              label: "Date",
              value:
                next_appointment?.date ? formatDate(next_appointment.date) : "—",
            },
            { label: "Type", value: next_appointment?.type },
            { label: "Provider", value: next_appointment?.provider },
          ]}
        />
      </Card>
    </Space>
  );
};

const KeyClinicalThemesSection = ({ data }) => {
  if (!data || typeof data !== "object") {
    return <Empty description="No key clinical themes" />;
  }

  const { primary_concerns, treatment_progression, response_to_interventions } =
    data;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="Primary Concerns">
        {Array.isArray(primary_concerns) && primary_concerns.length > 0 ? (
          <List
            dataSource={primary_concerns}
            renderItem={(c, i) => (
              <List.Item key={i} style={{ paddingLeft: 0 }}>
                <Text>• {c}</Text>
              </List.Item>
            )}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No concerns listed"
          />
        )}
      </Card>

      <Card title="Treatment Progression">
        <Paragraph style={{ marginBottom: 0 }}>
          {treatment_progression &&
          String(treatment_progression).trim().length > 0
            ? treatment_progression
            : "—"}
        </Paragraph>
      </Card>

      <Card title="Response to Interventions">
        <Paragraph style={{ marginBottom: 0 }}>
          {response_to_interventions &&
          String(response_to_interventions).trim().length > 0
            ? response_to_interventions
            : "—"}
        </Paragraph>
      </Card>
    </Space>
  );
};

const PastSummaryView = ({ pastSummary }) => {
  const pastSummaryRef = useVisibilityTracking('past-summary-view');

  if (!pastSummary) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Empty description="No summary available" />
      </div>
    );
  }

  const { clinical_timeline, current_status, key_clinical_themes } =
    pastSummary || {};

  const [activeKeys, setActiveKeys] = React.useState([]);

  const isExpanded = (key) =>
    Array.isArray(activeKeys) ? activeKeys.includes(key) : activeKeys === key;

  const items = [
    {
      key: "clinical_timeline",
      label: (
        <Space>
          <Text metrics-label="View Clinical Timeline" strong>
            Clinical Timeline
          </Text>
          <Text
            type="secondary"
            metrics-label={`Clinical Timeline ${isExpanded("clinical_timeline") ? " Collapsed" : " Expanded"}`}
          >
            {isExpanded("clinical_timeline") ? " Collapsed" : " Expanded"}
          </Text>
        </Space>
      ),
      children: <ClinicalTimelineSection data={clinical_timeline} />,
    },
    {
      key: "current_status",
      label: (
        <Space>
          <Text metrics-label="View Current Status" strong>
            Current Status
          </Text>
          <Text
            type="secondary"
            metrics-label={`Current Status ${isExpanded("current_status") ? " Collapsed" : " Expanded"}`}
          >
            {isExpanded("current_status") ? " Collapsed" : " Expanded"}
          </Text>
        </Space>
      ),
      children: <CurrentStatusSection data={current_status} />,
    },
    {
      key: "key_clinical_themes",
      label: (
        <Space>
          <Text metrics-label="View Key Clinical Themes" strong>
            Key Clinical Themes
          </Text>
          <Text
            type="secondary"
            metrics-label={`Key Clinical Themes ${isExpanded("key_clinical_themes") ? " Collapsed" : " Expanded"}`}
          >
            {isExpanded("key_clinical_themes") ? " Collapsed" : " Expanded"}
          </Text>
        </Space>
      ),
      children: <KeyClinicalThemesSection data={key_clinical_themes} />,
    },
  ];

  return (
    <div ref={pastSummaryRef} style={{ padding: 16 }}>
      <Title level={3} style={{ marginTop: 0, marginBottom: 12 }}>
        Patient Summary
      </Title>

      <Collapse
        items={items}
        activeKey={activeKeys}
        onChange={(keys) => setActiveKeys(keys)}
      />
    </div>
  );
};

export default PastSummaryView;
